import { useEffect, useRef, useState } from 'react';
import { Search, Filter } from 'react-feather';
import { X } from 'lucide-react';
import type { FilterSectionProps } from './filter-section.props.ts';
import FilterDropdown from '../filter-dropdown/index.tsx';

const SEARCH_DEBOUNCE_MS = 400;

function FilterSection({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  filters,
  hasActiveFilters,
  onClearFilters,
}: FilterSectionProps) {
  const hasFilters = filters && filters.length > 0;
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setLocalSearchValue(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
      debounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleClearSearch = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setLocalSearchValue('');
    onSearchChange('');
  };

  return (
    <div
      className={hasFilters ? 'border border-gray-200 rounded-lg bg-white shadow-sm mb-6' : 'mb-6'}
    >
      {hasFilters && (
        <div className="pt-2 px-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            Filters
          </h2>
          {hasActiveFilters && onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-[#EF4444] text-sm font-medium hover:underline"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
      <div className={hasFilters ? 'p-3' : ''}>
        <div className={hasFilters ? 'grid grid-cols-1 md:grid-cols-3 gap-4' : 'w-full'}>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-800" />
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={localSearchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-3 py-1.5 border border-gray-200 rounded-lg bg-white text-gray-800 placeholder-[#6A6A6A] focus:outline-none focus:ring-2 focus:ring-[#0A8BC7] focus:border-transparent"
            />
            {localSearchValue && (
              <button
                type="button"
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-800"
              >
                <X className="w-4 h-4 text-gray-400" />
              </button>
            )}
          </div>
          {filters.map((filter) =>
            filter.multiple ? (
              <FilterDropdown
                key={filter.placeholder}
                multiple
                value={filter.value}
                onChange={filter.onChange}
                options={filter.options}
                placeholder={filter.placeholder}
                clearValue={filter.clearValue ?? 'all'}
                showClear={filter.showClear ?? true}
                selectAllLabel={filter.selectAllLabel}
                usePortal
              />
            ) : (
              <FilterDropdown
                key={filter.placeholder}
                value={filter.value}
                onChange={filter.onChange}
                options={filter.options}
                placeholder={filter.placeholder}
                clearValue={filter.clearValue ?? 'all'}
                showClear={filter.showClear ?? true}
              />
            ),
          )}
        </div>
      </div>
    </div>
  );
}

export default FilterSection;
